import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Activity, User, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VisitLogs({ credentials }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/activity/logs`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      } else {
        toast.error('Failed to load visit logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load visit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/activity/logs/download`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `visit_logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Visit logs downloaded successfully');
      } else {
        toast.error('Failed to download visit logs');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download visit logs');
    } finally {
      setIsDownloading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata'
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading visit logs...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Visit Logs
            </CardTitle>
            <CardDescription>
              Track user visits to the website (one entry per user per day)
            </CardDescription>
          </div>
          <Button 
            onClick={handleDownloadCSV}
            disabled={isDownloading || logs.length === 0}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? 'Downloading...' : 'Download CSV'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No visit logs yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Activity className="h-4 w-4" />
                  Total Visits
                </div>
                <div className="text-2xl font-bold">{logs.length}</div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <User className="h-4 w-4" />
                  Unique Users
                </div>
                <div className="text-2xl font-bold">
                  {new Set(logs.map(log => log.userEmail)).size}
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Calendar className="h-4 w-4" />
                  Today's Visits
                </div>
                <div className="text-2xl font-bold">
                  {logs.filter(log => {
                    const today = new Date().toISOString().split('T')[0];
                    return log.visitDate === today;
                  }).length}
                </div>
              </div>
            </div>

            {/* Logs Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                <table className="w-full">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Name</th>
                      <th className="text-left p-3 text-sm font-medium">Email</th>
                      <th className="text-left p-3 text-sm font-medium">Phone</th>
                      <th className="text-left p-3 text-sm font-medium">Visit Date</th>
                      <th className="text-left p-3 text-sm font-medium">Visit Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log, index) => (
                      <tr 
                        key={index} 
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm font-medium">
                          {log.userName || 'Unknown User'}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {log.userEmail || 'unknown'}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {log.userPhone || '-'}
                        </td>
                        <td className="p-3 text-sm">
                          {log.visitDate}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {formatTimestamp(log.visitTimestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
